import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { setIsAppInstalled } from "src/redux/slices/AppSlice";

const APP_DEEP_LINK = "beemz://beemxyz.com/";
const isAndroid = /Android/i.test(navigator.userAgent);
const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * Hook to detect if the beemz native app is installed on mobile devices.
 * Uses deep link trigger + visibilitychange heuristic:
 * - iOS: hidden iframe with deep link
 * - Android: programmatic link click
 * - document.hidden after trigger = app opened = installed
 * - No visibility change within timeout = not installed
 */
export function useAppDetection() {
  const dispatch = useDispatch();

  useEffect(() => {
    const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobileDevice) {
      dispatch(setIsAppInstalled(false));
      return;
    }

    dispatch(setIsAppInstalled(null));
    let timeout;
    let detectionComplete = false;

    const handleVisibilityChange = () => {
      if (detectionComplete) return;
      clearTimeout(timeout);
      if (document.hidden) {
        detectionComplete = true;
        dispatch(setIsAppInstalled(true));
        localStorage.setItem("beemzAppInstalled", "true");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const checkAppInstalled = () => {
      if (isIOS) {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = APP_DEEP_LINK;
        document.body.appendChild(iframe);
        setTimeout(() => document.body.removeChild(iframe), 100);
      } else if (isAndroid) {
        const link = document.createElement("a");
        link.href = APP_DEEP_LINK;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => document.body.removeChild(link), 100);
      }

      timeout = setTimeout(() => {
        if (!detectionComplete) {
          detectionComplete = true;
          dispatch(setIsAppInstalled(false));
          localStorage.setItem("beemzAppInstalled", "false");
        }
      }, 1500);
    };

    setTimeout(checkAppInstalled, 100);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);
}

export const APP_URLS = {
  deepLink: APP_DEEP_LINK,
  playStore: "https://play.google.com/store/apps/details?id=com.beemxyz",
  appStore: "https://apps.apple.com/us/app/beemz/id1609866877",
};
